import React, { useState, useEffect, useRef } from "react";

const Partners = ({ partners = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Duplicate partners if there are too few to create a proper 3D loop effect
  const displayPartners = React.useMemo(() => {
    if (!partners || partners.length === 0) return [];
    if (partners.length === 1) return partners;
    if (partners.length === 2) return [...partners, ...partners, ...partners];
    if (partners.length === 3) return [...partners, ...partners];
    return partners;
  }, [partners]);

  const totalPartners = displayPartners.length;

  useEffect(() => {
    if (totalPartners <= 1 || isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalPartners);
    }, 2500);

    return () => clearInterval(interval);
  }, [totalPartners, isPaused]);

  if (totalPartners === 0) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalPartners);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPartners) % totalPartners);
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext();
    }
    if (touchStartX.current - touchEndX.current < -50) {
      handlePrev();
    }
    setIsPaused(false);
  };

  const getCardStyle = (index) => {
    let offset = (index - currentIndex) % totalPartners;
    if (offset < 0) offset += totalPartners;
    
    // Convert to relative positioning: e.g., -2, -1, 0, 1, 2
    if (offset > Math.floor(totalPartners / 2)) {
      offset -= totalPartners;
    }

    const isActive = offset === 0;
    const isVisible = Math.abs(offset) <= 2;

    let translateX = `${offset * 65}%`;
    let scale = 1 - Math.abs(offset) * 0.2; // 1, 0.8, 0.6
    let opacity = isActive ? 1 : Math.max(0, 1 - Math.abs(offset) * 0.4); // 1, 0.6, 0.2, 0
    let zIndex = 20 - Math.abs(offset);

    if (Math.abs(offset) > 2) {
      opacity = 0;
      scale = 0;
    }

    return {
      transform: `translateX(calc(-50% + ${translateX})) scale(${Math.max(0, scale)})`,
      opacity,
      zIndex,
      pointerEvents: isActive ? "auto" : "none",
      visibility: isVisible ? "visible" : "hidden",
    };
  };

  return (
    <div 
      className="premium-partners-carousel"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="carousel-track">
        {displayPartners.map((partner, index) => (
          <div 
            key={`${partner._id}-${index}`} 
            className={`carousel-card ${index === currentIndex ? 'active' : ''}`}
            style={getCardStyle(index)}
          >
            <div className="card-content">
              <img src={partner?.image} alt={partner?.name || "Partner"} className="partner-logo" />
              {partner?.name && <h4 className="partner-name">{partner.name}</h4>}
            </div>
          </div>
        ))}
      </div>
      
      {totalPartners > 1 && (
        <>
          <button className="nav-btn prev-btn" onClick={handlePrev} aria-label="Previous Partner">
            <i className="fa fa-chevron-left"></i>
          </button>
          <button className="nav-btn next-btn" onClick={handleNext} aria-label="Next Partner">
            <i className="fa fa-chevron-right"></i>
          </button>
        </>
      )}
    </div>
  );
};

export default Partners;